import { AicLockState, useAicLockStore } from "@/common/stores/nonpersistingStores/aicLock";

/**
 * Returns whether AI features should be locked for a specific interaction.
 *
 * Grandfathering is anchored on the FIRST agent assignment (never overwritten on transfer),
 * so callers should pass `firstAgentAssignedAt` from the session. An interaction is locked when:
 * - The AIC lock is currently active, AND
 * - The interaction has not yet been agent-assigned (null), OR was first assigned AT/AFTER the
 *   lock was applied.
 *
 * Interactions that were first assigned to an agent before the lock remain fully functional.
 */
export const useIsAicLockedForInteraction = (firstAgentAssignedAt?: string): boolean => {
	const { isAicLocked, lockedAt } = useAicLockStore((state: AicLockState) => state);

	if (!isAicLocked) {
		return false;
	}

	if (!lockedAt || !firstAgentAssignedAt) {
		// Fail-closed: missing lockedAt/firstAgentAssignedAt is treated as locked when AIC is active.
		return true;
	}

	const assignedDate = new Date(firstAgentAssignedAt);
	const lockDate = new Date(lockedAt);

	if (isNaN(assignedDate.getTime()) || isNaN(lockDate.getTime())) {
		// Fail-closed: an unparseable timestamp is treated as locked when AIC is active.
		return true;
	}

	return assignedDate >= lockDate;
};
